<script setup lang="ts">
// Vendor
import type { ComponentInternalInstance } from "vue";

// Types
import type { FormControlProps } from "../types/formcontrol";

// Constants
import { FORM_CONTROL_FIELD_SYMBOL } from "../constants/symbols";
import { FORM_CONTROL_ERROR_CLASS } from "../shared/constants";

const {
  hint,
  id,
  label,
  name,
  validateOnValueUpdate = false,
} = defineProps<FormControlProps>();

if (!name) {
  throw new Error("[FormControl]: name is required");
}

const slots = defineSlots<{
  default(): (ComponentInternalInstance | Element)[];
}>();

const field = useField(() => name, undefined, {
  validateOnValueUpdate,
});

const errorMessage = computed(() => field.errorMessage.value);

const formControlClasses = computed(() => [
  "form-control",
  {
    [FORM_CONTROL_ERROR_CLASS]:
      (field.meta.touched && !field.meta.valid) || errorMessage.value,
    "form-control--has-value": !!field.value.value,
  },
]);

const labelSuffix = computed(() => (field.meta.required ? "*" : ""));

const hasRadioOrCheckbox = computed(() => {
  if (!slots.default) {
    return false;
  }

  return slots.default().some((vnode) => {
    if (!("type" in vnode)) {
      return false;
    }

    return ["FormCheckbox", "FormRadio"].includes(
      vnode.type?.__name || "VueComponent",
    );
  });
});

// Provide field props to child input
provide(FORM_CONTROL_FIELD_SYMBOL, {
  ...field,
  inputId: id,
  validateOnValueUpdate,
});

// Expose field values
defineExpose({
  field,
});
</script>

<template>
  <div :class="formControlClasses">
    <div class="form-control__input-wrapper">
      <slot />
      <label
        v-if="label"
        :class="[
          'form-control__label',
          !hasRadioOrCheckbox && 'form-control__label--floating',
        ]"
        :for="id || name"
        >{{ label }}{{ labelSuffix }}</label
      >
    </div>
    <div v-if="errorMessage || hint" class="form-control__footer">
      <div class="form-control__hint" v-if="hint">{{ hint }}</div>
      <div class="form-control__error-message" v-if="errorMessage">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
%floating-label-active {
  transform: translateY(0) scale(0.65);
}

.form-control {
  font-family: inherit;

  &__error-message,
  &__hint {
    font-size: var(--input-font-size-supportive, 0.8rem);
  }

  &__error-message {
    color: var(--primary-red, #ff0038);
  }

  &__footer {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--input-footer-gap, 2px);
    margin-top: var(--input-footer-offset, 4px);
  }

  &__input-wrapper {
    position: relative;
    display: grid;

    /* Focus state */
    :deep(.form-control__native-el--has-focus),
    :deep(input:focus),
    :deep(select:focus),
    :deep(textarea:focus),
    :deep(select) {
      & ~ .form-control__label--floating {
        @extend %floating-label-active;
      }
    }
  }

  &__label {
    font-weight: var(--input-font-weight, 500);
    line-height: var(--input-label-line-height, 1.5);
  }

  &__label--floating {
    position: absolute;
    top: 4px;
    left: var(--input-padding-inline, 16px);
    transform: translateY(var(--input-label-offset, 14px));
    transform-origin: left;
    transition-timing-function: var(
      --input-label-transition-timing-function,
      ease-in-out
    );
    transition-duration: var(--input-label-transition-duration, 0.2s);
    transition-property: transform;
  }

  /* States */
  &--has-value &__input-wrapper &__label--floating {
    @extend %floating-label-active;
  }

  &--has-error {
    --input-border-color: var(--primary-red, #ff0038);
  }
}
</style>
